import React from 'react';
import { COLORS } from '../../../../theme';
import {
    Box,
    Button,
    Typography,
} from '@mui/material';
import * as Yup from 'yup';
import { useThemeColors } from '../../../../hooks/useThemeColors';
import allForm from '../../../Stateless/Form';
import { toast } from 'react-hot-toast';

interface FormValues {
    hyperpayStatus: string;
    cashOnDeliveryStatus: string;
}

interface PaymentSettingsProps {
    onClose?: () => void;
}

const validationSchema = Yup.object().shape({
    hyperpayStatus: Yup.string()
        .oneOf(['Enable', 'Disable'], 'Invalid Hyperpay Status')
        .required('Hyperpay status is required'),
    cashOnDeliveryStatus: Yup.string()
        .oneOf(['Enable', 'Disable'], 'Invalid Cash On Delivery Status')
        .required('Cash On Delivery status is required'),
});

const PaymentSettings: React.FC<PaymentSettingsProps> = ({ onClose }) => {
    const { warning } = useThemeColors();

    const initialValues: FormValues = {
        hyperpayStatus: 'Enable',
        cashOnDeliveryStatus: 'Enable',
    };

    const handleHyperpaySubmit = async (values: FormValues) => {
        try {
            console.log('Hyperpay settings to save:', {
                hyperpayStatus: values.hyperpayStatus,
            });
            toast.success('Hyperpay settings saved successfully');
        } catch (error) {
            console.error('Error saving Hyperpay settings:', error);
            toast.error('Failed to save Hyperpay settings');
        }
    };

    const handleCashOnDeliverySubmit = async (values: FormValues) => {
        try {
            // TODO: Implement API call to save Cash on Delivery settings
            console.log('Cash on Delivery settings to save:', {
                cashOnDeliveryStatus: values.cashOnDeliveryStatus,
            });
            toast.success('Cash on Delivery settings saved successfully');
        } catch (error) {
            console.error('Error saving Cash on Delivery settings:', error);
            toast.error('Failed to save Cash on Delivery settings');
        }
    };

    const allFields = [
        {
            name: 'hyperpaySection',
            type: 'section',
            sectionTitle: 'Hyperpay',
            isRequired: true,
            sectionLine: true,
        },
        {
            name: 'hyperpayStatus',
            label: 'Hyperpay Status',
            type: 'radio',
            isRequired: true,
            radioOptions: ['Enable', 'Disable'],
        },
        {
            name: 'hyperpaySubmitButton',
            type: 'custom',
            component: ({ form }: { form: any }) => (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 3 }}>
                    <Button
                        type="button"
                        variant="contained"
                        sx={{
                            backgroundColor: COLORS.primary.main,
                            textTransform: 'none',
                            '&:hover': { backgroundColor: COLORS.primary.dark },
                        }}
                        onClick={() => handleHyperpaySubmit(form.values)}
                    >
                        Submit
                    </Button>
                </Box>
            ),
        },
        {
            name: 'cashOnDeliverySection',
            type: 'section',
            sectionTitle: 'CashOnDelivery Status',
            sectionLine: true,
        },
        {
            name: 'cashOnDeliveryStatus',
            label: 'CashOnDelivery Status',
            type: 'radio',
            radioOptions: ['Enable', 'Disable'],
        },
        {
            name: 'cashOnDeliverySubmitButton',
            type: 'custom',
            component: ({ form }: { form: any }) => (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 3 }}>
                    <Button
                        type="button"
                        variant="contained"
                        sx={{
                            backgroundColor: COLORS.primary.main,
                            textTransform: 'none',
                            '&:hover': { backgroundColor: COLORS.primary.dark },
                        }}
                        onClick={() => handleCashOnDeliverySubmit(form.values)}
                    >
                        Submit
                    </Button>
                </Box>
            ),
        },
    ];

    const formConfig = {
        initialValues,
        validationSchema,
        onSubmit: (values: Record<string, any>) => {
            // This is not used since we have individual submit buttons
            console.log('Form submitted:', values);
        },
        fields: allFields,
        validateOnChange: true, // Enable real-time validation
        validateOnBlur: true,   // Enable validation on blur
        customButton: () => (
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3 }}>
                {onClose && (
                    <Button
                        variant="contained"
                        sx={{ bgcolor: warning.main }}
                        onClick={() => onClose()}
                        type="button"
                    >
                        Cancel
                    </Button>
                )}
            </Box>
        ),
    };

    return (
        <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
            <Typography
                variant="h4"
            >
                Payment Settings
            </Typography>
            {allForm(formConfig)()}
        </Box>
    );
};

export default PaymentSettings;
